import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  title: string;
  description?: string;
  type: ToastType;
  onClose: (id: string) => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  id,
  title,
  description,
  type,
  onClose,
  duration = 5000
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        onClose(id);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isClosing, onClose, id]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getClasses = () => {
    const baseClasses = 'max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto overflow-hidden transform transition-all duration-300 ease-in-out';
    const animationClasses = isClosing
      ? 'translate-x-full opacity-0'
      : 'translate-x-0 opacity-100';
    return `${baseClasses} ${animationClasses}`;
  };

  return (
    <div className={getClasses()}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">{getIcon()}</div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            {description && (
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => setIsClosing(true)}
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ToastOptions {
  title: string;
  description?: string;
  type: ToastType;
  duration?: number;
}

type ToastState = ToastOptions & { id: string };

let toasts: ToastState[] = [];
let listeners: ((toasts: ToastState[]) => void)[] = [];

const notifyListeners = () => {
  listeners.forEach((listener) => listener([...toasts]));
};

export const toast = ({ title, description, type, duration }: ToastOptions) => {
  const id = Math.random().toString(36).substr(2, 9);
  toasts = [...toasts, { id, title, description, type, duration }];
  notifyListeners();
  return id;
};

export const useToast = () => {
  const [toastList, setToastList] = useState<ToastState[]>(toasts);

  useEffect(() => {
    const listener = (updatedToasts: ToastState[]) => {
      setToastList(updatedToasts);
    };

    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  const removeToast = (id: string) => {
    toasts = toasts.filter((toast) => toast.id !== id);
    notifyListeners();
  };

  return { toasts: toastList, removeToast };
};

export const Toaster: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed inset-0 flex flex-col items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-50 space-y-4">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          title={toast.title}
          description={toast.description}
          type={toast.type}
          onClose={removeToast}
          duration={toast.duration}
        />
      ))}
    </div>
  );
};